import { authService } from '../services/auth.service';
import { router } from '../utils/router.util';
import { formatPhone, cleanPhone, isValidPhone } from '../utils/phone.util';
import { ButtonComponent } from '../components/button.component';
import { getErrorMessage } from '../utils/error.util';
import { ROUTES } from '../constants/routes.constants';

type LoginStep = 'sms-phone' | 'sms-code';

export class LoginPage {
  private container: HTMLElement;
  private currentStep: LoginStep = 'sms-phone';
  private phoneNumber: string = '';
  private resendTimer: number | null = null;
  private resendCountdown: number = 60;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    // Mostrar formulário SMS diretamente
    this.currentStep = 'sms-phone';
    this.renderContent();
  }

  private renderContent(): void {
    if (this.currentStep === 'sms-phone') {
      this.renderSMSPhone();
    } else if (this.currentStep === 'sms-code') {
      this.renderSMSCode();
    }
  }

  private renderSMSPhone(): void {
    this.container.innerHTML = `
      <div class="container" style="display: flex; justify-content: center; align-items: flex-start; min-height: 80vh; padding: 80px 20px 20px 20px;">
        <div style="width: 100%; max-width: 100%;">
          <div style="text-align: left; margin-bottom: 32px;">
            <h1 class="welcome-title" style="margin-bottom: 40px;">
              Boas-vindas
            </h1>
            <h3 style="margin-bottom: 0; text-align: left;">
              Pra começar, é só inserir suas informações
            </h3>
            <p class="text-preset-2-regular" style="margin-bottom: 40px; text-align: left;">
              Insira um número elegível
            </p>
          </div>
          <form id="phone-form">
            <label class="has-float-label phone-input-label" style="display: block; margin-bottom: 24px;">
              <input 
                type="tel" 
                id="phone-input" 
                placeholder="(00) 00000-0000"
                maxlength="15"
                required
              />
              <span>Número Vivo Móvel</span>
            </label>
            <div id="send-sms-btn-container"></div>
            <div id="phone-error" class="error" style="display: none; margin-top: 16px;"></div>
          </form>
        </div>
      </div>
      <style>
        .phone-input-label input {
          width: 100%;
          padding: 14px;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 16px;
          font-family: var(--font-family-vivo);
          outline: none;
          transition: all 0.2s ease;
          background: transparent;
          margin-bottom: 0;
        }
        .phone-input-label input:placeholder-shown:not(:focus) {
          padding-top: 14px;
          padding-bottom: 14px;
        }
        .phone-input-label input:not(:placeholder-shown),
        .phone-input-label input:focus {
          padding-top: 32px;
          padding-bottom: 12px;
        }
        .phone-input-label input:focus {
          border-color: var(--color-primary);
          border-width: 2px;
          outline: none;
        }
        .phone-input-label > span {
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #666666;
          font-size: 14px;
          font-weight: 400;
          pointer-events: none;
          transition: all 0.2s ease;
        }
        .phone-input-label input:placeholder-shown:not(:focus) + span {
          font-size: 16px;
          opacity: 1;
          top: 50%;
          transform: translateY(-50%);
          color: #666666;
        }
        .phone-input-label input:not(:placeholder-shown) + span,
        .phone-input-label input:focus + span {
          font-size: 12px;
          top: 12px;
          transform: translateY(0);
          color: var(--color-primary);
        }
      </style>
    `;

    this.attachPhoneButton();
    this.attachPhoneEvents();
  }

  private attachPhoneButton(): void {
    const container = document.getElementById('send-sms-btn-container');
    if (container) {
      const button = ButtonComponent.createPrimaryButton({
        text: 'Continuar',
        type: 'submit',
        fullWidth: true,
        id: 'send-sms-btn'
      });
      container.appendChild(button);
    }
  }

  private renderSMSCode(): void {
    const maskedPhone = this.getMaskedPhone(this.phoneNumber);
    
    this.container.innerHTML = `
      <div class="container" style="display: flex; justify-content: center; align-items: flex-start; min-height: 80vh; padding: 80px 20px 20px 20px;">
        <div style="width: 100%; max-width: 100%;">
          <div style="text-align: left; margin-bottom: 32px;">
            <h1 class="welcome-title" style="margin-bottom: 40px;">
              Boas-vindas
            </h1>
            <h3 style="margin-bottom: 0; text-align: left;">
              Agora, insira o código enviado por SMS
            </h3>
            <p class="text-preset-2-regular" style="margin-bottom: 40px; text-align: left;">
              Você só vai receber o código se o número ${maskedPhone} for um número Vivo
            </p>
          </div>
          <form id="code-form">
            <div style="margin-bottom: 24px;">
              <div style="display: flex; gap: 8px; justify-content: center; margin-bottom: 24px;">
                <input type="text" id="code-0" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
                <input type="text" id="code-1" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
                <input type="text" id="code-2" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
                <input type="text" id="code-3" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
                <input type="text" id="code-4" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
                <input type="text" id="code-5" class="code-input" maxlength="1" inputmode="numeric" pattern="[0-9]" autocomplete="off" />
              </div>
              <div id="resend-timer" class="text-preset-2-regular" style="text-align: left; margin-bottom: 8px;">
                ${this.formatTimer(this.resendCountdown)} para pedir novo código
              </div>
              <button type="button" id="resend-code-btn" style="display: none; width: 100%; background: transparent; border: none; color: var(--color-primary); font-size: 14px; font-weight: 400; cursor: pointer; padding: 0; text-align: center; margin-bottom: 8px;">
                Pedir novo código
              </button>
            </div>
            <div id="validate-code-btn-container"></div>
            <div id="back-to-phone-btn-container" style="margin-top: 8px;"></div>
            <div id="code-error" class="error" style="display: none; margin-top: 16px;"></div>
          </form>
        </div>
      </div>
      <style>
        .code-input {
          width: 48px;
          height: 48px;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 24px;
          font-weight: 500;
          text-align: center;
          font-family: var(--font-family-vivo);
          outline: none;
          transition: border-color 0.2s;
        }
        .code-input:focus {
          border-color: var(--color-primary);
          border-width: 2px;
        }
        .code-input:invalid {
          border-color: #c33;
        }
        .resend-btn-loading {
          position: relative;
          color: transparent !important;
        }
        .resend-btn-spinner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 16px;
          height: 16px;
          border: 2px solid rgba(102, 0, 153, 0.3);
          border-top-color: #660099;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          display: block;
        }
      </style>
    `;

    this.attachCodeEvents();
    this.attachCodeButton();
    this.attachBackButton();
    this.startResendTimer();
  }

  private attachCodeButton(): void {
    const container = document.getElementById('validate-code-btn-container');
    if (container) {
      const button = ButtonComponent.createPrimaryButton({
        text: 'Continuar',
        type: 'submit',
        fullWidth: true,
        id: 'validate-code-btn'
      });
      container.appendChild(button);
    }
  }

  private attachBackButton(): void {
    const container = document.getElementById('back-to-phone-btn-container');
    if (container) {
      const button = ButtonComponent.createSecondaryButton({
        text: 'Voltar',
        type: 'button',
        fullWidth: true,
        id: 'back-to-phone-btn',
        onClick: () => {
          if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
          }
          this.currentStep = 'sms-phone';
          this.renderContent();
        }
      });
      container.appendChild(button);
    }
  }

  private getMaskedPhone(phone: string): string {
    const clean = cleanPhone(phone);
    if (clean.length !== 11) return phone;
    
    const ddd = clean.slice(0, 2);
    const lastFour = clean.slice(7);
    return `(${ddd}) *****${lastFour}`;
  }

  private formatTimer(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  private startResendTimer(): void {
    this.resendCountdown = 60;
    const timerElement = document.getElementById('resend-timer');
    const resendBtn = document.getElementById('resend-code-btn');
    
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }

    this.resendTimer = window.setInterval(() => {
      this.resendCountdown--;
      
      if (timerElement) {
        if (this.resendCountdown > 0) {
          timerElement.textContent = `${this.formatTimer(this.resendCountdown)} para pedir novo código`;
          timerElement.style.display = 'block';
          if (resendBtn) {
            resendBtn.style.display = 'none';
          }
        } else {
          timerElement.style.display = 'none';
          if (resendBtn) {
            resendBtn.style.display = 'block';
          }
          if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
          }
        }
      }
    }, 1000);
  }

  private attachPhoneEvents(): void {
    const phoneInput = document.getElementById('phone-input') as HTMLInputElement;
    const phoneForm = document.getElementById('phone-form') as HTMLFormElement;
    const sendSmsBtn = document.getElementById('send-sms-btn') as HTMLButtonElement;
    const errorDiv = document.getElementById('phone-error');

    // Aplicar máscara de telefone
    if (phoneInput) {
      phoneInput.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        target.value = formatPhone(target.value);
      });
    }

    // Enviar código SMS
    if (phoneForm) {
      phoneForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        if (!phoneInput) return;

        const phone = phoneInput.value;
        
        if (!isValidPhone(phone)) {
          if (errorDiv) {
            errorDiv.textContent = 'Número de telefone inválido. Use o formato (XX) XXXXX-XXXX';
            errorDiv.style.display = 'block';
          }
          return;
        }

        if (errorDiv) errorDiv.style.display = 'none';
        if (sendSmsBtn) {
          ButtonComponent.setLoading(sendSmsBtn, true, 'Continuar');
        }

        try {
          this.phoneNumber = cleanPhone(phone);
          await authService.requestSMSCode(this.phoneNumber);
          
          // Ir para tela de código
          this.currentStep = 'sms-code';
          this.renderContent();
        } catch (error: unknown) {
          if (errorDiv) {
            const errorMessage = getErrorMessage(error);
            errorDiv.textContent = errorMessage;
            errorDiv.style.display = 'block';
          }
        } finally {
          if (sendSmsBtn) {
            ButtonComponent.setLoading(sendSmsBtn, false, 'Continuar');
          }
        }
      });
    }
  }

  private attachCodeEvents(): void {
    const codeInputs = [
      document.getElementById('code-0') as HTMLInputElement,
      document.getElementById('code-1') as HTMLInputElement,
      document.getElementById('code-2') as HTMLInputElement,
      document.getElementById('code-3') as HTMLInputElement,
      document.getElementById('code-4') as HTMLInputElement,
      document.getElementById('code-5') as HTMLInputElement,
    ];
    const codeForm = document.getElementById('code-form') as HTMLFormElement;
    const resendCodeBtn = document.getElementById('resend-code-btn') as HTMLButtonElement;
    const errorDiv = document.getElementById('code-error');

    // Focar no primeiro input
    if (codeInputs[0]) {
      setTimeout(() => codeInputs[0].focus(), 100);
    }

    // Gerenciar inputs de código (navegação entre campos)
    codeInputs.forEach((input, index) => {
      if (!input) return;

      input.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const value = target.value.replace(/\D/g, '').slice(0, 1);
        target.value = value;

        // Mover para próximo campo se digitou
        if (value && index < codeInputs.length - 1) {
          codeInputs[index + 1]?.focus();
        }
      });

      input.addEventListener('keydown', (e) => {
        // Voltar para campo anterior ao pressionar Backspace
        if (e.key === 'Backspace' && !input.value && index > 0) {
          codeInputs[index - 1]?.focus();
        }
        // Mover para campo anterior com seta esquerda
        if (e.key === 'ArrowLeft' && index > 0) {
          codeInputs[index - 1]?.focus();
        }
        // Mover para próximo campo com seta direita
        if (e.key === 'ArrowRight' && index < codeInputs.length - 1) {
          codeInputs[index + 1]?.focus();
        }
      });

      input.addEventListener('paste', (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData?.getData('text') || '';
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);
        
        digits.split('').forEach((digit, i) => {
          if (codeInputs[i]) {
            codeInputs[i].value = digit;
          }
        });

        // Focar no último campo preenchido ou no próximo vazio
        const nextEmptyIndex = digits.length < 6 ? digits.length : 5;
        codeInputs[nextEmptyIndex]?.focus();
      });
    });

    // Validar código
    if (codeForm) {
      codeForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const code = codeInputs.map(input => input.value).join('');
        
        if (code.length !== 6) {
          if (errorDiv) {
            errorDiv.textContent = 'Código deve ter 6 dígitos';
            errorDiv.style.display = 'block';
          }
          return;
        }

        if (errorDiv) errorDiv.style.display = 'none';
        
        // Ativar loading no botão
        const button = document.getElementById('validate-code-btn') as HTMLButtonElement;
        if (button) {
          ButtonComponent.setLoading(button, true, 'Continuar');
        }

        try {
          await authService.loginWithSMS(this.phoneNumber, code);
          
          // Login bem-sucedido, redirecionar para home
          router.navigate(ROUTES.HOME);
        } catch (error: unknown) {
          if (errorDiv) {
            const errorMessage = getErrorMessage(error);
            errorDiv.textContent = errorMessage;
            errorDiv.style.display = 'block';
          }
          // Limpar campos em caso de erro
          codeInputs.forEach(input => {
            if (input) input.value = '';
          });
          if (codeInputs[0]) codeInputs[0].focus();
        } finally {
          // Desativar loading no botão
          const buttonAfter = document.getElementById('validate-code-btn') as HTMLButtonElement;
          if (buttonAfter) {
            ButtonComponent.setLoading(buttonAfter, false, 'Continuar');
          }
        }
      });
    }

    // Reenviar código
    if (resendCodeBtn) {
      resendCodeBtn.addEventListener('click', async () => {
        if (errorDiv) errorDiv.style.display = 'none';
        
        // Salvar texto original e mostrar loading
        const originalText = resendCodeBtn.textContent || 'Pedir novo código';
        resendCodeBtn.disabled = true;
        resendCodeBtn.dataset.originalText = originalText;
        
        // Aplicar loading no botão de reenvio
        resendCodeBtn.classList.add('resend-btn-loading');
        resendCodeBtn.innerHTML = '<span class="resend-btn-spinner"></span>';
        resendCodeBtn.style.color = 'transparent';
        resendCodeBtn.style.position = 'relative';

        try {
          await authService.requestSMSCode(this.phoneNumber);
          
          // Reiniciar timer e ocultar botão
          const timerElement = document.getElementById('resend-timer');
          if (timerElement) {
            timerElement.style.display = 'block';
          }
          if (resendCodeBtn) {
            resendCodeBtn.style.display = 'none';
          }
          this.startResendTimer();
          
          // Limpar campos
          codeInputs.forEach(input => {
            if (input) input.value = '';
          });
          if (codeInputs[0]) codeInputs[0].focus();
        } catch (error: unknown) {
          if (errorDiv) {
            const errorMessage = getErrorMessage(error);
            errorDiv.textContent = errorMessage;
            errorDiv.style.display = 'block';
          }
          // Restaurar botão em caso de erro
          const savedText = resendCodeBtn.dataset.originalText || originalText;
          resendCodeBtn.textContent = savedText;
          resendCodeBtn.style.color = '';
          resendCodeBtn.style.position = '';
          resendCodeBtn.classList.remove('resend-btn-loading');
          resendCodeBtn.disabled = false;
          delete resendCodeBtn.dataset.originalText;
        }
        // Nota: Se o código foi enviado com sucesso, o botão é ocultado,
        // então não precisamos restaurar o estado no finally
      });
    }

  }
}
